import React, { useEffect, useMemo } from "react";

import { DEFAULT_LIST_LIMIT, RunSequence, State } from "react-lib/apps/MEH/sequence/Wellness";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import formatComma from "react-lib/utils/formatComma";

// UX
import CardWellnessUX from "react-lib/apps/MEH/CardWellnessUX";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";
import { useIntl } from "react-intl";

// Types
export type CardWellnessProps = {
  setProp: (key: string, value: any) => any;
  onEvent: (e: any) => any;
  // seq
  runSequence: RunSequence;
  // data
  WellnessSequence: State["WellnessSequence"];
  // CommonInterface
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  // callback
  onClose?: (changed: boolean) => any;
  languageUX?: string;
};

const CARD_WELLNESS = "CardWellness";

const CardWellness = (props: CardWellnessProps) => {
  const intl = useIntl();

  useEffect(() => {
    props.runSequence({
      sequence: "Wellness",
      action: "SEARCH",
      card: CARD_WELLNESS,
      restart: true,
    });
  }, []);

  const reportCoverageList = useMemo(() => {
    return (props.WellnessSequence?.reportCoverageList?.items || []).map((item) => {
      return {
        ...item,
        product_code: (
          <div style={{ display: "flex", justifyContent: "center" }}>{item.product_code}</div>
        ),
        service_date: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {dateToStringWithoutTimeBE(item.service_date)}
          </div>
        ),
        price: (
          <div style={{ display: "flex", justifyContent: "right" }}>
            {formatPaymentText(item.price)}
          </div>
        ),
      };
    });
  }, [props.WellnessSequence?.reportCoverageList?.items]);

  const handleChangeFilterData = (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    props.setProp(`WellnessSequence.filterWellness.${name}`, value);
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "Wellness",
      action: "SEARCH",
      card: CARD_WELLNESS,
    });
  };

  const handleDownloadXLSX = () => {
    props.runSequence({
      sequence: "Wellness",
      action: "DOWNLOAD_XLSX",
      card: CARD_WELLNESS,
    });
  };

  const handleClearData = () => {
    props.setProp("WellnessSequence.filterWellness", {
      start_date: "",
      end_date: "",
      product_code: "",
      invoice_code: "",
      receipt_code: "",
      patient_name: "",
    });
  };

  const handleChangeDate = (name: string) => (value: string) => {
    handleChangeFilterData(null, { value, name });
  };

  const handleChangePage = (activePage: number) => {
    props.runSequence({
      sequence: "Wellness",
      action: "SEARCH",
      card: CARD_WELLNESS,
      activePage,
    });
  };

  return (
    <div
      style={{
        height: "calc(105vh - 3rem)",
        overflow: "auto",
        paddingBottom: "3rem",
      }}
    >
      <SnackMessage
        onEvent={props.onEvent}
        error={props.errorMessage?.[CARD_WELLNESS]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_WELLNESS}`, null);
        }}
        languageUX={props.languageUX}
      />
      <CardWellnessUX
        filterWellness={props.WellnessSequence?.filterWellness}
        totalPrice={formatPaymentText(props.WellnessSequence?.reportCoverageList?.totalPrice || 0)}
        total={props.WellnessSequence?.reportCoverageList?.total || 0}
        activePage={props?.WellnessSequence?.reportCoverageList?.activePage || 1}
        limit={DEFAULT_LIST_LIMIT}
        reportCoverageList={reportCoverageList}
        onChange={handleChangeFilterData}
        onChangeStartDate={handleChangeDate("start_date")}
        onChangeEndDate={handleChangeDate("end_date")}
        clearData={handleClearData}
        pagination={
          <PaginationShorthand
            activePage={props?.WellnessSequence?.reportCoverageList?.activePage || 1}
            limit={DEFAULT_LIST_LIMIT}
            total={props?.WellnessSequence?.reportCoverageList?.total || 0}
            onPageChange={handleChangePage}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={handleSearch}
            paramKey={`${CARD_WELLNESS}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_WELLNESS}_SEARCH`]}
            style={{
              background: "var(--primary-theme-color)",
              color: "white",
            }}
            fluid
            icon="search"
            size="small"
            title={intl.formatMessage({ id: `ค้นหา` })}
          />
        }
        buttonDownloadExcel={
          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={handleDownloadXLSX}
            paramKey={`${CARD_WELLNESS}_DOWNLOAD_XLSX`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_WELLNESS}_DOWNLOAD_XLSX`]}
            fluid
            color="green"
            icon="file"
            size="small"
            title={"Export Excel"}
          />
        }
      />
    </div>
  );
};

const formatPaymentText = (data: any) => {
  return formatComma(Number(data).toFixed(2).replaceAll(",", ""));
};

CardWellness.displayName = "CardWellness";

export default React.memo(CardWellness);
